/**
 * MediaClock — a single continuous playback clock, modelled on the HTML
 * `<video>` media clock and the W3C Timing Object.
 *
 * Time is never "ticked" frame-by-frame; it is *computed* from wall-clock:
 *
 *     t = position + (now() - wallAnchor) * rate
 *
 * Pause = rate 0; scrub = set position; speed = set rate. Every consumer samples
 * `getTime()` against its own data, so a fixed render cadence can never alias the
 * (irregular) frame cadence — the bug that made frames repeat/skip on a 1s tick.
 *
 * Pure given an injected `now`, so it is fully unit-testable.
 */

export type Gap = [start: number, end: number];

interface Options {
  now?: () => number;
  position?: number;
}

export class MediaClock {
  private position: number;
  private wallAnchor: number;
  private _rate = 0; // 0 = paused
  private min = -Infinity;
  private max = Infinity;
  private gaps: Gap[] = [];
  private readonly now: () => number;
  private readonly listeners = new Set<() => void>();

  constructor(opts: Options = {}) {
    this.now = opts.now ?? (() => performance.now());
    this.position = opts.position ?? 0;
    this.wallAnchor = this.now();
  }

  /** Raw, unclamped media time. */
  private raw(): number {
    return this.position + (this.now() - this.wallAnchor) * this._rate;
  }

  /** Snap a time out of any gap (forward to the gap end), then clamp to bounds. */
  private normalize(t: number): number {
    let v = t < this.min ? this.min : t > this.max ? this.max : t;
    for (const [s, e] of this.gaps) {
      if (v > s && v < e) {
        v = e;
        break;
      }
    }
    return v;
  }

  /** Continuous current time (ms), clamped to bounds and snapped out of gaps. */
  getTime(): number {
    return this.normalize(this.raw());
  }

  /**
   * Advance the anchor past a gap or end the playhead has entered, so playback
   * *skips* (compresses out) gaps instead of dwelling at the gap edge. Call once
   * per sampling tick during playback. Returns true if it jumped.
   */
  settle(): boolean {
    const r = this.raw();
    for (const [s, e] of this.gaps) {
      if (r > s && r < e) {
        this.reanchor(e);
        return true;
      }
    }
    if (r > this.max) {
      this.reanchor(this.max);
      return true;
    }
    return false;
  }

  get rate(): number {
    return this._rate;
  }

  get paused(): boolean {
    return this._rate === 0;
  }

  /** Re-anchor so `raw()` continues from `to` (call before changing rate/position). */
  private reanchor(to: number): void {
    this.position = to;
    this.wallAnchor = this.now();
  }

  play(rate = 1): void {
    this.reanchor(this.getTime());
    this._rate = rate;
    this.emit();
  }

  pause(): void {
    this.reanchor(this.getTime());
    this._rate = 0;
    this.emit();
  }

  seek(t: number): void {
    this.reanchor(this.normalize(t));
    this.emit();
  }

  setRate(rate: number): void {
    this.reanchor(this.getTime());
    this._rate = rate;
    this.emit();
  }

  setBounds(min: number, max: number): void {
    this.min = min;
    this.max = max;
    // keep the current position valid
    this.reanchor(this.getTime());
    this.emit();
  }

  setGaps(gaps: Gap[]): void {
    this.gaps = gaps;
    this.reanchor(this.getTime());
    this.emit();
  }

  /** Notified on state changes (play/pause/seek/rate/bounds/gaps), not per tick. */
  subscribe(cb: () => void): () => void {
    this.listeners.add(cb);
    return () => this.listeners.delete(cb);
  }

  private emit(): void {
    this.listeners.forEach((cb) => cb());
  }
}
